import gql from 'graphql-tag';
import {
  ActionPlanTemplateSchema,
  ActionPlanTemplatesSchema,
  ActionPlanTemplateTasksSchema,
} from '../../definitions/schemas';
import Interface from '../Interface';

interface CreateActionPlanTemplateInput {
  description?: string;
  name: string;
  tasks: ActionPlanTemplateTasksSchema;
}

interface CreateActionPlanTemplateData {
  createActionPlanTemplate: {
    actionPlanTemplate: ActionPlanTemplateSchema;
  };
}

interface UpdateActionPlanTemplateInput extends CreateActionPlanTemplateInput {
  id: string;
}

interface UpdateActionPlanTemplateData {
  updateActionPlanTemplate: {
    actionPlanTemplate: ActionPlanTemplateSchema;
  };
}

interface DeleteActionPlanTemplateInput {
  id: string;
}

interface SearchActionPlanTemplatesData {
  actionPlanTemplates: ActionPlanTemplatesSchema;
}

interface GetActionPlanTemplateInput {
  id: string;
}

interface GetActionPlanTemplateData {
  getActionPlanTemplate: ActionPlanTemplateSchema;
}

const actionPlanTemplateBaseFields = `
  id
  actionPlansCount
  createdAt
  name
  description
  tasksCount
  updatedAt
`;

const actionPlanTemplateFields = `
  ${actionPlanTemplateBaseFields}
  tasks {
    description
    name
    milestone
    subtasks {
      description
      name
    }
  }
`;

export default class ActionPlanTemplatesInterface extends Interface {
  public async create(input: CreateActionPlanTemplateInput) {
    return this.mutate<CreateActionPlanTemplateData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation createActionPlanTemplate($input: CreateActionPlanTemplateInput!) {
            createActionPlanTemplate(input: $input) {
              actionPlanTemplate {
                ${actionPlanTemplateFields}
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: response.data,
        serverErrors: response.errors,
        serverMessage: response.errors?.map((e) => e.message).join(''),
      };
    }, 'Error creating action plan template');
  }

  public async update(input: UpdateActionPlanTemplateInput) {
    return this.mutate<UpdateActionPlanTemplateData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation updateActionPlanTemplate($input: UpdateActionPlanTemplateInput!) {
            updateActionPlanTemplate(input: $input) {
              actionPlanTemplate {
                ${actionPlanTemplateFields}
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: response.data,
        serverErrors: response.errors,
        serverMessage: response.errors?.map((e) => e.message).join(''),
      };
    }, 'Error updating action plan template');
  }

  public async delete(input: DeleteActionPlanTemplateInput) {
    return this.mutate<null>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation deleteActionPlanTemplate(
            $input: DeleteActionPlanTemplateInput!
          ) {
            deleteActionPlanTemplate(input: $input) {
              errors
              message
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: null,
        serverErrors: response.errors,
        serverMessage: response.errors?.map((e) => e.message).join(''),
      };
    }, 'Error deleting action plan template');
  }

  public async search() {
    const response = await this.query<SearchActionPlanTemplatesData>(
      () =>
        this.apolloService.executeQuery<SearchActionPlanTemplatesData>({
          query: gql`
            query actionPlanTemplates {
              actionPlanTemplates {
                ${actionPlanTemplateBaseFields}
              }
            }
          `,
        }),
      'Error getting action plan templates'
    );

    return response.actionPlanTemplates;
  }

  public async get(variables: GetActionPlanTemplateInput) {
    const response = await this.query<GetActionPlanTemplateData>(
      () =>
        this.apolloService.executeQuery<GetActionPlanTemplateData>({
          query: gql`
            query getActionPlanTemplate($id: ID!) {
              getActionPlanTemplate(id: $id) {
                ${actionPlanTemplateFields}
              }
            }
          `,
          variables,
        }),
      'Error getting action plan template',
      true
    );

    return response.getActionPlanTemplate;
  }
}
